import React from "react";
import { Box, Typography, LinearProgress } from "@mui/material";

interface WeightProgressProps {
  type: "weight";
  done: number;
  total: number;
  /** Optional label, defaults to "Weight" */
  label?: string;
  /** Decimal places for weight display, defaults to 1 */
  decimals?: number;
  /** Size variant */
  size?: "small" | "medium";
  /** Hide remaining info row */
  hideRemaining?: boolean;
}

interface RollsProgressProps {
  type: "rolls";
  done: number;
  total: number;
  /** Remaining weight for kg/roll calculation */
  remainingWeight: number;
  /** Optional label, defaults to "Rolls" */
  label?: string;
  /** Size variant */
  size?: "small" | "medium";
  /** Hide remaining info row */
  hideRemaining?: boolean;
  /** Hide kg/roll calculation */
  hideKgPerRoll?: boolean;
}

type ProgressBarProps = WeightProgressProps | RollsProgressProps;

const ProgressBar: React.FC<ProgressBarProps> = (props) => {
  const { type, label, size = "medium", hideRemaining = false } = props;

  // Ensure done and total are always numbers
  const done = Number(props.done) || 0;
  const total = Number(props.total) || 0;

  const remaining = total - done;
  const percentage = total > 0 ? (done / total) * 100 : 0;
  const clampedPercentage = Math.min(percentage, 100);
  const isComplete = percentage >= 100;
  const isOverflow = percentage > 100;

  // Get bar color based on progress
  const getBarColor = () => {
    if (isComplete) return "success.main";
    if (percentage >= 75) return "#4caf50"; // Green-ish for almost done
    if (percentage >= 50) return "primary.main";
    return "#ff9800"; // Orange for early progress
  };

  // Size configurations
  const config = {
    small: {
      barHeight: 6,
      labelSize: "caption" as const,
      valueSize: "caption" as const,
      spacing: 0.25,
    },
    medium: {
      barHeight: 8,
      labelSize: "body2" as const,
      valueSize: "body2" as const,
      spacing: 0.5,
    },
  };

  const { barHeight, labelSize, valueSize, spacing } = config[size];

  // Format remaining info based on type
  const getRemainingInfo = () => {
    if (type === "weight") {
      const decimals = (props as WeightProgressProps).decimals ?? 1;
      if (remaining <= 0) {
        return `✓ Complete${isOverflow ? ` (+${Math.abs(remaining).toFixed(decimals)} kg extra)` : ""}`;
      }
      return `${remaining.toFixed(decimals)} kg remaining`;
    } else {
      // Rolls type
      const rollsProps = props as RollsProgressProps;
      const remainingWeight = Number(rollsProps.remainingWeight) || 0;
      const hideKgPerRoll = rollsProps.hideKgPerRoll ?? false;
      if (remaining <= 0) {
        return `✓ Complete`;
      }
      const kgPerRoll = !hideKgPerRoll && remaining > 0 && remainingWeight > 0
        ? (remainingWeight / remaining).toFixed(1)
        : null;
      return kgPerRoll
        ? `${Math.round(remaining)} remaining @ ~${kgPerRoll} kg/roll`
        : `${Math.round(remaining)} remaining`;
    }
  };

  // Format the done/total display
  const getProgressText = () => {
    if (type === "weight") {
      const decimals = (props as WeightProgressProps).decimals ?? 1;
      return `${done.toFixed(decimals)} / ${total.toFixed(decimals)} kg`;
    }
    return `${Math.round(done)} / ${Math.round(total)}`;
  };

  const displayLabel = label ?? (type === "weight" ? "Weight" : "Rolls");

  return (
    <Box sx={{ width: "100%" }}>
      {/* Top row: Label on left, Done/Total on right */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: spacing,
        }}
      >
        <Typography
          variant={labelSize}
          sx={{
            fontWeight: 600,
            color: "text.secondary",
          }}
        >
          {displayLabel}
        </Typography>
        <Typography
          variant={valueSize}
          sx={{
            fontWeight: 500,
            color: isComplete ? "success.main" : "text.primary",
          }}
        >
          {getProgressText()}
        </Typography>
      </Box>

      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={clampedPercentage}
        sx={{
          height: barHeight,
          borderRadius: 1,
          bgcolor: "grey.200",
          "& .MuiLinearProgress-bar": {
            bgcolor: getBarColor(),
            borderRadius: 1,
          },
        }}
      />

      {/* Bottom row: Remaining info on left, Percentage on right */}
      {!hideRemaining && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: spacing,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: isComplete ? "success.main" : "text.secondary",
              fontWeight: isComplete ? 500 : 400,
            }}
          >
            {getRemainingInfo()}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: isComplete ? "success.main" : "text.secondary",
            }}
          >
            {percentage.toFixed(0)}%
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ProgressBar;

